import { GridElement, ElementAction } from '../types';
import { GpsBounds } from '../types/presenter.types';

/**
 * Returns default properties for different types of grid elements
 * These are sensible defaults that users can modify after creation
 * @param type - The type of element (power-plant, meter, transformer, etc.)
 * @returns Object with default properties for the element type
 */
export const getDefaultProperties = (type: string) => {
  switch (type) {
    case 'power-plant':
      return { power: 50, voltage: 400, efficiency: 85 }; // Power in kW, voltage in V, efficiency in %
    case 'pole':
      return { height: 8 }; // Height in meters
    case 'meter':
      return { consumption: 2.5, voltage: 230, mtr_number: '' }; // Consumption in kW, voltage in V, meter number
    case 'transformer':
      return { power: 25, voltage: 400 }; // Power rating in kVA, voltage in V
    default:
      return { voltage: 400 }; // Default voltage for unknown types
  }
};

/**
 * Calculates GPS bounds from all elements that have GPS coordinates
 * Used for coordinate conversion between canvas and GPS
 */
export const calculateGpsBounds = (elements: GridElement[]): GpsBounds => {
  const elementsWithGps = elements.filter(el => 
    el.action !== ElementAction.TO_DELETE &&
    el.gpsCoordinates && 
    el.gpsCoordinates.latitude !== 0 && 
    el.gpsCoordinates.longitude !== 0 &&
    !isNaN(el.gpsCoordinates.latitude) &&
    !isNaN(el.gpsCoordinates.longitude) &&
    el.gpsCoordinates.latitude !== null &&
    el.gpsCoordinates.longitude !== null
  );
  
  if (elementsWithGps.length === 0) {
    // Return default bounds for Paris area when no elements are present
    // This allows map interaction and element placement on empty maps
    return {
      minLat: 48.8,
      maxLat: 48.9,
      minLng: 2.2,
      maxLng: 2.4
    };
  }
  
  const lats = elementsWithGps.map(el => el.gpsCoordinates!.latitude);
  const lngs = elementsWithGps.map(el => el.gpsCoordinates!.longitude);
  
  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs)
  };
};

/**
 * Calculates the center of mass (centroid) of all elements with GPS coordinates
 * This gives a better center point when elements are clustered in specific areas
 */
export const calculateCenterOfMass = (elements: GridElement[]): { lat: number; lng: number } | null => {
  const elementsWithGps = elements.filter(el => 
    el.action !== ElementAction.TO_DELETE &&
    el.gpsCoordinates && 
    el.gpsCoordinates.latitude !== 0 && 
    el.gpsCoordinates.longitude !== 0 &&
    !isNaN(el.gpsCoordinates.latitude) &&
    !isNaN(el.gpsCoordinates.longitude) &&
    el.gpsCoordinates.latitude !== null &&
    el.gpsCoordinates.longitude !== null
  );
  
  if (elementsWithGps.length === 0) {
    return null;
  }
  
  // Calculate the center of mass (average of all coordinates)
  const totalLat = elementsWithGps.reduce((sum, el) => sum + el.gpsCoordinates!.latitude, 0);
  const totalLng = elementsWithGps.reduce((sum, el) => sum + el.gpsCoordinates!.longitude, 0);
  
  return {
    lat: totalLat / elementsWithGps.length,
    lng: totalLng / elementsWithGps.length
  };
};

/**
 * Fits the map view to show the specified geographic bounds
 * Automatically calculates appropriate zoom level and center point
 * @param bounds - Geographic bounding box to fit
 * @param elements - Optional array of elements to calculate center of mass and optimal zoom
 * @returns Object with new zoom, pan, and map center settings
 */
export const calculateFitMapSettings = (bounds: GpsBounds, elements?: GridElement[]) => {
  // Calculate the center point - prefer center of mass if elements are provided
  let centerLat: number;
  let centerLng: number;
  
  if (elements && elements.length > 0) {
    const centerOfMass = calculateCenterOfMass(elements);
    if (centerOfMass) {
      centerLat = centerOfMass.lat;
      centerLng = centerOfMass.lng;
    } else {
      // Fallback to geometric center
      centerLat = (bounds.minLat + bounds.maxLat) / 2;
      centerLng = (bounds.minLng + bounds.maxLng) / 2;
    }
  } else {
    // Use geometric center of bounds
    centerLat = (bounds.minLat + bounds.maxLat) / 2;
    centerLng = (bounds.minLng + bounds.maxLng) / 2;
  }
  
  // Calculate optimal zoom based on elements if provided, otherwise use bounds-based calculation
  let targetZoom: number;
  
  if (elements && elements.length > 0) {
    targetZoom = calculateOptimalZoom(elements, bounds);
  } else {
    // Fallback to bounds-based zoom calculation using the same logic as calculateOptimalZoom
    const latRange = Math.max(0.0001, bounds.maxLat - bounds.minLat);
    const lngRange = Math.max(0.0001, bounds.maxLng - bounds.minLng);
    const maxRange = Math.max(latRange, lngRange);
    
    // Use the same zoom calculation optimized for ultra-detailed street level
    if (maxRange < 0.0001) {
      targetZoom = 3.0;
    } else if (maxRange < 0.0003) {
      targetZoom = 3.0;
    } else if (maxRange < 0.0008) {
      targetZoom = 2.9;
    } else if (maxRange < 0.002) {
      targetZoom = 2.8;
    } else if (maxRange < 0.008) {
      targetZoom = 2.6;
    } else if (maxRange < 0.03) {
      targetZoom = 2.3;
    } else if (maxRange < 0.15) {
      targetZoom = 2.0;
    } else {
      targetZoom = 1.7;
    }
    
    targetZoom = Math.max(0.5, Math.min(3, targetZoom));
  }
  
  return {
    zoom: targetZoom,
    pan: { x: 0, y: 0 },
    mapCenter: { lat: centerLat, lng: centerLng }
  };
};

/**
 * Calculates the average distance between GPS points
 * Used to determine point density for zoom adjustment
 */
export const calculateAveragePointDistance = (elements: GridElement[]): number => {
  const elementsWithGps = elements.filter(el => 
    el.action !== ElementAction.TO_DELETE &&
    el.gpsCoordinates && 
    el.gpsCoordinates.latitude !== 0 && 
    el.gpsCoordinates.longitude !== 0
  );
  
  if (elementsWithGps.length < 2) {
    return 0;
  }
  
  let totalDistance = 0;
  let pairCount = 0;
  
  // Calculate distances between all pairs of points
  for (let i = 0; i < elementsWithGps.length; i++) {
    for (let j = i + 1; j < elementsWithGps.length; j++) {
      const lat1 = elementsWithGps[i].gpsCoordinates!.latitude;
      const lng1 = elementsWithGps[i].gpsCoordinates!.longitude;
      const lat2 = elementsWithGps[j].gpsCoordinates!.latitude;
      const lng2 = elementsWithGps[j].gpsCoordinates!.longitude;
      
      // Simple distance calculation (not precise but good enough for zoom)
      const distance = Math.sqrt(Math.pow(lat2 - lat1, 2) + Math.pow(lng2 - lng1, 2));
      totalDistance += distance;
      pairCount++;
    }
  }
  
  return pairCount > 0 ? totalDistance / pairCount : 0;
};

/**
 * Calculates optimal zoom level based on geographic bounds and point spacing
 * Adapts zoom to the distance between points for optimal visibility
 */
export const calculateOptimalZoom = (elements: GridElement[], bounds: GpsBounds): number => {
  const elementsWithGps = elements.filter(el => 
    el.action !== ElementAction.TO_DELETE &&
    el.gpsCoordinates && 
    el.gpsCoordinates.latitude !== 0 && 
    el.gpsCoordinates.longitude !== 0
  );
  
  if (elementsWithGps.length === 0) {
    return 3.0; // Maximum default zoom for empty maps (ultra-detailed street level)
  }
  
  if (elementsWithGps.length === 1) {
    return 3.0; // Maximum zoom for single element
  }
  
  // Calculate the geographic range
  const latRange = bounds.maxLat - bounds.minLat;
  const lngRange = bounds.maxLng - bounds.minLng;
  const maxRange = Math.max(latRange, lngRange);
  
  // Calculate average distance between points
  const averageDistance = calculateAveragePointDistance(elementsWithGps);
  
  // Base zoom calculation on geographic range
  let baseZoom: number;
  
  if (maxRange < 0.0001) {
    baseZoom = 3.0;
  } else if (maxRange < 0.0003) {
    baseZoom = 3.0;
  } else if (maxRange < 0.0008) {
    baseZoom = 2.9;
  } else if (maxRange < 0.002) {
    baseZoom = 2.8;
  } else if (maxRange < 0.008) {
    baseZoom = 2.6;
  } else if (maxRange < 0.03) {
    baseZoom = 2.3;
  } else if (maxRange < 0.15) {
    baseZoom = 2.0;
  } else if (maxRange < 0.5) {
    baseZoom = 1.5;
  } else if (maxRange < 1.0) {
    baseZoom = 1.2;
  } else if (maxRange < 5.0) {
    baseZoom = 0.8;
  } else if (maxRange < 20.0) {
    baseZoom = 0.6;
  } else {
    baseZoom = 0.5;
  }
  
  // Adjust zoom based on point spacing
  let optimalZoom = baseZoom;
  
  if (elementsWithGps.length >= 2) {
    // If points are very close together, zoom in more
    if (averageDistance < 0.0001) {
      optimalZoom = Math.min(3.0, baseZoom + 0.3);
    } else if (averageDistance < 0.0005) {
      optimalZoom = Math.min(3.0, baseZoom + 0.2);
    } else if (averageDistance < 0.001) {
      optimalZoom = Math.min(3.0, baseZoom + 0.1);
    } else if (averageDistance > 10) {
      // If points are on different continents (extreme distance), use minimum zoom
      optimalZoom = 0.5;
    } else if (averageDistance > 5) {
      // If points are very far apart (different countries), zoom out significantly
      optimalZoom = Math.max(0.5, baseZoom - 1.2);
    } else if (averageDistance > 1) {
      // If points are far apart (different regions), zoom out more
      optimalZoom = Math.max(0.5, baseZoom - 1.0);
    } else if (averageDistance > 0.5) {
      // If points are very far apart (different cities), zoom out moderately
      optimalZoom = Math.max(0.5, baseZoom - 0.8);
    } else if (averageDistance > 0.1) {
      // If points are far apart (different districts), zoom out slightly
      optimalZoom = Math.max(0.5, baseZoom - 0.6);
    } else if (averageDistance > 0.05) {
      optimalZoom = Math.max(0.5, baseZoom - 0.4);
    } else if (averageDistance > 0.02) {
      optimalZoom = Math.max(0.5, baseZoom - 0.3);
    } else if (averageDistance > 0.01) {
      optimalZoom = Math.max(0.5, baseZoom - 0.2);
    } else if (averageDistance > 0.005) {
      optimalZoom = Math.max(0.5, baseZoom - 0.1);
    } else if (averageDistance > 0.002) {
      optimalZoom = Math.max(0.5, baseZoom - 0.05);
    }
  }
  
  // Clamp to reasonable bounds
  return Math.max(0.5, Math.min(3, optimalZoom));
};

/**
 * Synchronise automatiquement le champ number avec mtr_number pour les meters
 * Cette fonction assure que le champ number de l'élément reste synchronisé avec le mtr_number
 * @param element - L'élément à synchroniser
 * @returns L'élément avec les champs synchronisés
 */
export const synchronizeMeterNumber = (element: GridElement): GridElement => {
  if (element.type === 'meter') {
    // Si l'élément est un meter, synchroniser le champ number avec mtr_number
    const mtrNumber = String(element.properties?.mtr_number || '');
    
    // Si mtr_number existe et number est vide ou différent, mettre à jour number
    if (mtrNumber && (!element.number || element.number !== mtrNumber)) {
      return {
        ...element,
        number: mtrNumber
      };
    }
    
    // Si number existe et mtr_number est vide ou différent, mettre à jour mtr_number
    if (element.number && (!element.properties?.mtr_number || element.properties.mtr_number !== element.number)) {
      return {
        ...element,
        properties: {
          ...element.properties,
          mtr_number: String(element.number)
        }
      };
    }
  }
  
  return element;
};

/**
 * Synchronise tous les éléments d'un tableau, en particulier les meters
 * @param elements - Tableau d'éléments à synchroniser
 * @returns Tableau d'éléments avec les champs synchronisés
 */
export const synchronizeAllMeterNumbers = (elements: GridElement[]): GridElement[] => {
  return elements.map(element => synchronizeMeterNumber(element));
};

/**
 * Calculates the current geographic bounds visible in the map
 * Used for determining which map tiles to load
 */
export const calculateMapBounds = (zoom: number, mapCenter: { lat: number; lng: number }) => {
  // Calculate the geographic range based on current zoom (0.05 is base range)
  const latRange = 0.05 / zoom;
  const lngRange = 0.05 / zoom;
  
  return {
    minLat: mapCenter.lat - latRange,
    maxLat: mapCenter.lat + latRange,
    minLng: mapCenter.lng - lngRange,
    maxLng: mapCenter.lng + lngRange
  };
};
